// The agent detail and policy editor, `/agents/$agentId` (spec 14 §4.3, D-512
// rulings 3, 6 and 7): the header (status toggle when the agent can be
// enabled, name, role badge), where it works, its caps (`agents.config.manage`),
// the tool policy matrix and the prompt tab. Read on `agents.read`.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { StatusChip } from "../../../../components/status-chip";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
} from "../../../../components/states";
import { Tabs } from "../../../../components/tabs";
import { toast } from "../../../../components/toast";
import { enumLabel } from "../../../../lib/enum-label";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { CapsCard, MembershipsCard } from "../components/agent-settings-cards";
import { PolicyMatrix } from "../components/policy-matrix";
import { PromptTab } from "../components/prompt-tab";
import { agentsKeys, httpStatusOf, patchAgent, useAgent } from "../hooks/agents-queries";
import { useAgentsPermissions } from "../hooks/permissions";
import { canToggleStatus, toggledStatus, type AgentDetail } from "../lib/agents-lib";
import { agentName, errorText } from "../lib/labels";

type DetailTab = "policies" | "prompt";

function Header({ agent, canManage }: { agent: AgentDetail; canManage: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(agent.display_name ?? "");
  const [busy, setBusy] = useState(false);
  const roles = [...new Set(agent.memberships.map((m) => m.role_code))];

  const patch = (body: Parameters<typeof patchAgent>[1], done: string) => {
    setBusy(true);
    void patchAgent(agent.id, body)
      .then(() => {
        toast.success(done);
      })
      .catch((error: unknown) => {
        toast.error(errorText(t, error));
      })
      .finally(() => {
        setBusy(false);
        void queryClient.invalidateQueries({ queryKey: agentsKeys.all });
      });
  };

  const trimmed = name.trim();
  const renamed = trimmed !== (agent.display_name ?? "");

  return (
    <Card data-testid="agent-header">
      <Card.Body className="ag-stack">
        <div className="ag-card-head">
          <div className="ag-row">
            <StatusChip
              variant={
                agent.status === "enabled" ? "ok" : agent.status === "paused" ? "neutral" : "bad"
              }
              data-testid="agent-status"
            >
              {t(`agents.status.${agent.status}`)}
            </StatusChip>
            {roles.map((role) => (
              <StatusChip key={role} variant="accent" dot={false} data-testid="agent-role">
                {enumLabel(t, "agents.roles", role)}
              </StatusChip>
            ))}
          </div>
          {canToggleStatus(agent, canManage) ? (
            <label className="ag-toggle">
              <input
                type="checkbox"
                role="switch"
                checked={agent.status === "enabled"}
                disabled={busy}
                aria-label={t("agents.hub.toggleLabel", { name: agentName(t, agent) })}
                onChange={() => {
                  const next = toggledStatus(agent.status);
                  patch(
                    { status: next, version: agent.version },
                    t("agents.hub.statusSaved", {
                      name: agentName(t, agent),
                      status: t(`agents.status.${next}`),
                    }),
                  );
                }}
                data-testid="agent-status-toggle"
              />
              {agent.status === "enabled" ? t("agents.hub.pause") : t("agents.hub.enable")}
            </label>
          ) : null}
        </div>
        {canManage ? (
          <div className="ag-field-row">
            <Field label={t("agents.detail.displayName")}>
              <input
                className="ds-input"
                maxLength={80}
                value={name}
                placeholder={t("agents.detail.renamePlaceholder")}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                data-testid="agent-rename"
              />
            </Field>
            {renamed ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => {
                  patch(
                    { display_name: trimmed === "" ? null : trimmed, version: agent.version },
                    t("agents.detail.renamed"),
                  );
                }}
                data-testid="agent-rename-save"
              >
                {t("agents.detail.rename")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

export function AgentDetailScreen({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useAgentsPermissions();
  const agent = useAgent(agentId);
  const [tab, setTab] = useState<DetailTab>("policies");

  useEffect(() => {
    if (agent.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [agent.error, navigate]);

  if (!perms.canRead) {
    return (
      <ForbiddenState
        title={t("agents.common.forbiddenTitle")}
        body={t("agents.common.forbiddenBody")}
      />
    );
  }
  if (agent.isPending) {
    return (
      <div className="ag-stack" data-testid="agent-detail-loading">
        <CardSkeleton lines={4} label={t("agents.detail.loading")} />
        <CardSkeleton lines={8} label={t("agents.detail.loading")} />
      </div>
    );
  }
  if (agent.isError) {
    if (httpStatusOf(agent.error) === 404) {
      return (
        <EmptyState
          title={t("agents.common.notFoundTitle")}
          body={t("agents.common.notFoundBody")}
        />
      );
    }
    return (
      <ErrorState
        title={t("agents.detail.loadError")}
        retryLabel={t("agents.common.retry")}
        onRetry={() => {
          void agent.refetch();
        }}
        requestId={requestIdOf(agent.error)}
        requestIdLabel={t("agents.common.requestId")}
      />
    );
  }

  const data = agent.data;
  return (
    <div className="ag-stack" data-testid="agent-detail">
      <PageHeader
        title={agentName(t, data)}
        crumbs={[{ label: t("agents.detail.back"), to: "/agents" }]}
      />
      <Header key={data.version} agent={data} canManage={perms.canManage} />
      <div className="ag-header-grid">
        <MembershipsCard key={`m-${data.version}`} agent={data} canManage={perms.canManage} />
        {perms.canConfig ? <CapsCard key={`c-${data.version}`} agent={data} /> : null}
      </div>
      <Tabs
        label={t("agents.detail.tabs.aria")}
        active={tab}
        onSelect={(id) => {
          setTab(id === "prompt" ? "prompt" : "policies");
        }}
        tabs={[
          {
            id: "policies",
            label: t("agents.detail.tabs.policies"),
            "data-testid": "agent-tab-policies",
          },
          {
            id: "prompt",
            label: t("agents.detail.tabs.prompt"),
            "data-testid": "agent-tab-prompt",
          },
        ]}
      />
      {tab === "policies" ? (
        <PolicyMatrix agent={data} canManage={perms.canManage} canConfig={perms.canConfig} />
      ) : (
        <PromptTab agent={data} canConfig={perms.canConfig} />
      )}
    </div>
  );
}
